import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  addGroupMembers,
  createGroupChat,
  fetchChatList,
  fetchChatMessages,
  fetchChatUsers,
  fetchPotentialMembers,
  markChatReadRest,
  removeGroupMember,
  sendChatMessageRest,
  startChat,
} from "../../services/chatApi";
import {
  connectChatSocket,
  joinChatRoom,
  markChatReadSocket,
  onChatMessage,
  onChatRead,
  onChatMemberRemoved,
  onPresenceUpdate,
  requestPresence,
  sendChatMessageSocket,
  isChatSocketConnected,
} from "../../services/chatSocket";
import { onSosSocketStatus } from "../../services/sosSocket";
import { getToken, getUserId } from "../../utils/authStorage";
import { image } from "../../Basurl/Baseurl";
import EmojiPicker from "emoji-picker-react";
import "./Messages.css";

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const NAME_PREFIXES = new Set([
  "pt",
  "pt.",
  "mr",
  "mr.",
  "mrs",
  "mrs.",
  "ms",
  "ms.",
  "miss",
  "dr",
  "dr.",
  "sir",
  "madam",
]);

const getInitials = (name = "") => {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !NAME_PREFIXES.has(part.toLowerCase().replace(/,$/, "")));
  if (!parts.length) {
    const fallback = String(name).trim();
    return fallback ? fallback.slice(0, 2).toUpperCase() : "?";
  }
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const resolveAttachmentUrl = (url) => {
  if (!url) return "";
  let strUrl = "";
  if (typeof url === "string") {
    strUrl = url;
  } else if (typeof url === "object") {
    strUrl =
      url.url ||
      url.path ||
      url.file ||
      url.filePath ||
      url.location ||
      url.src ||
      url.uri ||
      "";
  }
  if (!strUrl || typeof strUrl !== "string") return "";

  if (
    strUrl.startsWith("http://") ||
    strUrl.startsWith("https://") ||
    strUrl.startsWith("blob:") ||
    strUrl.startsWith("data:")
  ) {
    return strUrl;
  }

  let cleanPath = strUrl.replace(/^\/+/, "");
  if (cleanPath.startsWith("omca_crm/")) {
    cleanPath = cleanPath.replace(/^omca_crm\//, "");
  }

  return `${image}${cleanPath}`;
};

const isImageAttachment = (url = "", type = "", name = "") => {
  if (type && String(type).startsWith("image/")) return true;
  let str = "";
  if (typeof url === "string") str = url;
  else if (url && typeof url === "object") {
    str = url.url || url.path || url.file || url.name || "";
  }
  const target = `${str} ${name || ""}`.toLowerCase();
  return (
    /\.(jpeg|jpg|gif|png|webp|svg|bmp|jfif|ico|avif)($|\?)/i.test(target) ||
    str.startsWith("data:image/")
  );
};

const getFileIcon = (url = "", type = "", name = "") => {
  let str = "";
  if (typeof url === "string") str = url;
  else if (url && typeof url === "object") {
    str = url.url || url.path || url.file || url.name || "";
  }
  const target = `${str} ${type || ""} ${name || ""}`.toLowerCase();
  if (target.includes("pdf")) return "fa fa-file-pdf text-danger";
  if (
    target.includes("doc") ||
    target.includes("docx") ||
    target.includes("word")
  )
    return "fa fa-file-word text-primary";
  if (
    target.includes("xls") ||
    target.includes("xlsx") ||
    target.includes("sheet") ||
    target.includes("csv")
  )
    return "fa fa-file-excel text-success";
  if (
    target.includes("ppt") ||
    target.includes("pptx") ||
    target.includes("presentation")
  )
    return "fa fa-file-powerpoint text-warning";
  if (
    target.includes("zip") ||
    target.includes("rar") ||
    target.includes("tar") ||
    target.includes("7z") ||
    target.includes("gz")
  )
    return "fa fa-file-archive text-warning";
  if (
    target.includes("audio") ||
    target.includes("mp3") ||
    target.includes("wav") ||
    target.includes("m4a") ||
    target.includes("ogg")
  )
    return "fa fa-file-audio text-info";
  if (
    target.includes("video") ||
    target.includes("mp4") ||
    target.includes("mov") ||
    target.includes("mkv") ||
    target.includes("webm")
  )
    return "fa fa-file-video text-secondary";
  return "fa fa-file-alt text-secondary";
};

const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return "";
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const ChatAvatar = ({ name, src, size = "", isGroup = false }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!src && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [src]);

  if (isGroup) {
    return (
      <div className={`crm-chat-avatar is-group ${size}`}>
        <i className="fa fa-users" />
      </div>
    );
  }

  return (
    <div className={`crm-chat-avatar ${size} ${showImage ? "has-image" : ""}`}>
      {showImage ? (
        <img
          src={resolveAttachmentUrl(src)}
          alt={name || "User"}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span>{getInitials(name)}</span>
      )}
    </div>
  );
};

const normalizeChat = (item = {}) => {
  const isGroup = !!(
    item.isGroup ||
    item.status === "group" ||
    String(item.chatId || "").startsWith("group_")
  );
  const other = item.otherUser || {};
  const isOnline = isGroup
    ? false
    : !!(
      item.isOnline ??
      other.isOnline ??
      (item.status === "online" || other.status === "online")
    );
  let chatName = "";
  if (isGroup) {
    chatName = item.chatName || item.name || "Group Conversation";
  } else {
    // For 1-to-1 direct chats, show ONLY the other user's name
    if (other.name && typeof other.name === "string" && !other.name.includes(" & ")) {
      chatName = other.name;
    } else {
      const raw = String(other.name || item.chatName || item.name || "").trim();
      if (raw.includes(" & ")) {
        const parts = raw.split(" & ").map((p) => p.trim());
        const otherPart = parts.find(
          (p) => !/admin/i.test(p) && !/crm/i.test(p) && p.toLowerCase() !== "you",
        );
        chatName = otherPart || parts[1] || parts[0];
      } else {
        chatName = raw || `Patient ${other.userId || ""}`;
      }
    }
  }

  const profileImage =
    other.profileImage ||
    other.profile_image ||
    other.image ||
    item.profileImage ||
    null;

  return {
    chatId: item.chatId,
    chatName,
    isGroup,
    admin: String(item.admin || item.createdBy || item.adminId || ""),
    members: (() => {
      const raw =
        item.members ||
        item.participants ||
        item.users ||
        item.groupMembers ||
        [];
      return Array.isArray(raw)
        ? raw.map((m) => {
          if (typeof m !== "object" || !m) return m;
          const uid = String(m.userId || m.user_id || m._id || m.id || "");
          return {
            userId: uid,
            name:
              m.name ||
              m.patient_name ||
              m.fullname ||
              m.username ||
              m.enq_userName ||
              `User ${uid}`,
            email: m.email || "",
            phone: m.phone || m.mobile || m.contact_no || "",
            image: m.profileImage || m.profile_image || m.image || null,
          };
        }).filter((m) => m && (typeof m !== "object" || m.userId))
        : [];
    })(),
    memberIds: (() => {
      const raw =
        item.memberIds ||
        item.member_ids ||
        item.members ||
        item.users ||
        item.participants ||
        item.groupMembers ||
        item.user_ids ||
        [];
      return Array.isArray(raw)
        ? raw
          .map((m) =>
            String(
              typeof m === "object" ? m.userId || m.user_id || m._id || m.id : m,
            ),
          )
          .filter(Boolean)
        : [];
    })(),
    lastMessage: item.lastMessage || (item.attachment ? "📎 Attachment" : ""),
    lastMessageAt: item.lastMessageAt || item.updatedAt || null,
    unreadCount: Number(item.unreadCount || 0),
    isOnline,
    status: isGroup
      ? "group"
      : isOnline
        ? "online"
        : item.status || other.status || "offline",
    otherUser: isGroup
      ? null
      : {
        userId: String(other.userId ?? item.contactUserId ?? ""),
        name: chatName,
        profileImage: profileImage || null,
        isOnline,
        status: isOnline ? "online" : other.status || "offline",
        lastActivity: other.lastActivity || null,
      },
  };
};

/** Socket/REST payloads sometimes nest the message object. */
const unwrapRawMessage = (raw) => {
  if (!raw || typeof raw !== "object") return raw || {};
  if (
    raw.message &&
    typeof raw.message === "object" &&
    !Array.isArray(raw.message)
  ) {
    return {
      ...raw.message,
      chatId: raw.chatId || raw.message.chatId,
    };
  }
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    return unwrapRawMessage(raw.data);
  }
  if (
    raw.payload &&
    typeof raw.payload === "object" &&
    !Array.isArray(raw.payload)
  ) {
    return unwrapRawMessage(raw.payload);
  }
  return raw;
};

const extractAttachmentInfo = (msg = {}) => {
  let rawAttachment =
    msg.attachment ??
    msg.attachmentUrl ??
    msg.file ??
    msg.mediaUrl ??
    msg.media ??
    msg.image ??
    msg.document ??
    msg.doc ??
    msg.filePath ??
    msg.url ??
    null;

  if (Array.isArray(rawAttachment)) {
    rawAttachment = rawAttachment[0];
  } else if (Array.isArray(msg.attachments) && msg.attachments.length > 0) {
    rawAttachment = msg.attachments[0];
  } else if (Array.isArray(msg.files) && msg.files.length > 0) {
    rawAttachment = msg.files[0];
  }

  let attachmentUrl = "";
  let attachmentName =
    msg.attachmentName ||
    msg.fileName ||
    msg.filename ||
    msg.name ||
    msg.originalName ||
    msg.originalname ||
    "";
  let attachmentType =
    msg.attachmentType ||
    msg.fileType ||
    msg.mimeType ||
    msg.mimetype ||
    msg.type ||
    "";
  let attachmentSize =
    msg.attachmentSize ||
    msg.fileSize ||
    msg.size ||
    null;

  if (typeof rawAttachment === "string") {
    attachmentUrl = rawAttachment;
  } else if (rawAttachment && typeof rawAttachment === "object") {
    attachmentUrl =
      rawAttachment.url ||
      rawAttachment.path ||
      rawAttachment.file ||
      rawAttachment.filePath ||
      rawAttachment.filename ||
      rawAttachment.fileName ||
      rawAttachment.location ||
      rawAttachment.src ||
      rawAttachment.uri ||
      "";
    attachmentName =
      attachmentName ||
      rawAttachment.name ||
      rawAttachment.fileName ||
      rawAttachment.filename ||
      rawAttachment.originalName ||
      rawAttachment.originalname ||
      "";
    attachmentType =
      attachmentType ||
      rawAttachment.type ||
      rawAttachment.mimeType ||
      rawAttachment.mimetype ||
      rawAttachment.fileType ||
      "";
    attachmentSize =
      attachmentSize ||
      rawAttachment.size ||
      rawAttachment.fileSize ||
      null;
  }

  if (!attachmentName && attachmentUrl && typeof attachmentUrl === "string") {
    attachmentName = attachmentUrl.split("/").pop() || "Attachment";
  }

  return {
    attachment: attachmentUrl || null,
    attachmentName,
    attachmentType,
    attachmentSize,
  };
};

const normalizeMessage = (raw = {}, crmUserId) => {
  const msg = unwrapRawMessage(raw);
  const senderId = String(
    msg.senderUserId ??
    msg.senderId ??
    msg.fromUserId ??
    msg.sender?._id ??
    msg.sender?.id ??
    "",
  );
  const text = String(msg.message ?? msg.text ?? "").trim();
  const createdAt =
    msg.createdAt || msg.timestamp || msg.date || new Date().toISOString();
  const mine =
    senderId === String(crmUserId) ||
    msg.isMine === true ||
    msg.senderRole === "crm" ||
    msg.senderType === "crm" ||
    msg.sender === "crm";
  const status = String(msg.status || msg.readStatus || "").toLowerCase();
  const isRead = !!(
    msg.isRead === true ||
    msg.read === true ||
    msg.seen === true ||
    msg.isSeen === true ||
    status === "read" ||
    status === "seen"
  );

  const { attachment, attachmentName, attachmentType, attachmentSize } =
    extractAttachmentInfo(msg);

  const isGroup = !!(
    msg.isGroup ||
    String(msg.chatId || "").startsWith("group_")
  );

  const senderName =
    msg.senderName ||
    msg.sender?.name ||
    msg.sender?.patient_name ||
    (mine ? "You" : "");

  const msgId = String(
    msg._id ||
    msg.id ||
    msg.messageId ||
    msg.msgId ||
    msg.uuid ||
    `msg-${senderId}-${createdAt}-${attachmentName || ""}-${text.slice(0, 20)}-${Math.random().toString(36).slice(2, 7)}`,
  );

  return {
    _id: msgId,
    chatId: msg.chatId,
    message: text,
    attachment,
    attachmentName,
    attachmentType,
    attachmentSize,
    senderUserId: senderId,
    senderName,
    receiverUserId: String(msg.receiverUserId ?? msg.receiverId ?? ""),
    isRead,
    isGroup,
    createdAt,
    mine,
  };
};

const dedupeMessageList = (list = []) => {
  const seenIds = new Set();
  const out = [];
  for (const m of list) {
    if (!m) continue;
    if (m._id && seenIds.has(m._id)) continue;
    if (m._id) seenIds.add(m._id);
    out.push(m);
  }
  return out;
};

/** Merge a realtime message into local state without doubling echoes. */
const mergeIncomingMessage = (prev = [], msg) => {
  if (!msg) return prev;
  if (!msg.message && !msg.attachment) return prev;

  // 1. If exact ID exists, update it in-place
  if (msg._id && prev.some((m) => m._id === msg._id)) {
    return prev.map((m) =>
      m._id === msg._id
        ? {
          ...m,
          ...msg,
          isRead: !!(m.isRead || msg.isRead),
          createdAt: msg.createdAt || m.createdAt,
        }
        : m,
    );
  }

  // 2. Check if this resolves a specific pending optimistic message
  const msgTs = new Date(msg.createdAt).getTime();
  let replacedOptimistic = false;

  const updated = prev.map((m) => {
    if (
      !replacedOptimistic &&
      String(m._id).startsWith("local-") &&
      m.mine &&
      String(m.chatId) === String(msg.chatId)
    ) {
      const mTs = new Date(m.createdAt).getTime();
      const timeDiff = Math.abs(mTs - msgTs);
      const isTimeClose = Number.isNaN(timeDiff) || timeDiff < 60000;

      const bothAttachments = !!(m.attachment && msg.attachment);
      const matchingText = !!(
        m.message &&
        msg.message &&
        m.message === msg.message
      );

      if ((bothAttachments || matchingText) && isTimeClose) {
        replacedOptimistic = true;
        return {
          ...m,
          ...msg,
          _id: msg._id || m._id,
          isRead: !!(m.isRead || msg.isRead),
          createdAt: msg.createdAt || m.createdAt,
        };
      }
    }
    return m;
  });

  if (replacedOptimistic) {
    // If multiple duplicate instances of msg._id exist, keep only the first
    const seen = new Set();
    return updated.filter((m) => {
      if (!m._id) return true;
      if (seen.has(m._id)) return false;
      seen.add(m._id);
      return true;
    });
  }

  // 3. Prevent duplicate: if msg._id is already present, don't append again
  if (msg._id && prev.some((m) => m._id === msg._id)) {
    return prev;
  }

  // 4. Otherwise append as a new distinct message
  return [...prev, msg];
};

/** WhatsApp-style ticks — only for CRM (mine) messages. */
const MessageTicks = ({ msg }) => {
  if (!msg?.mine) return null;
  const pending = String(msg._id || "").startsWith("local-");
  const seen = !!msg.isRead;
  return (
    <span
      className={`crm-chat-ticks ${pending ? "is-pending" : seen ? "is-seen" : "is-unseen"}`}
      title={pending ? "Sending…" : seen ? "Seen" : "Sent"}
      aria-label={pending ? "Sending" : seen ? "Seen" : "Unseen"}
    >
      {pending ? (
        <i className="fa fa-check" />
      ) : (
        <i className="fas fa-check-double" />
      )}
    </span>
  );
};

export default function Messages() {
  const location = useLocation();
  const navigate = useNavigate();
  const crmUserId = getUserId();
  const messagesEndRef = useRef(null);
  const activeChatIdRef = useRef(null);
  const sendingRef = useRef(false);
  const seenMessageIdsRef = useRef(new Set());
  const fileInputRef = useRef(null);

  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [chatFilterTab, setChatFilterTab] = useState("all"); // 'all' | 'direct' | 'groups'
  const [listLoading, setListLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [socketOnline, setSocketOnline] = useState(false);
  const [userRemovedFromActiveChat, setUserRemovedFromActiveChat] =
    useState(false);

  // Modals state
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isGroupInfoOpen, setIsGroupInfoOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Create Group Form state
  const [newGroupName, setNewGroupName] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateMembers, setCandidateMembers] = useState([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Add Members state
  const [isAddMemberSectionOpen, setIsAddMemberSectionOpen] = useState(false);
  const [addMemberSelectedIds, setAddMemberSelectedIds] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target) &&
        !e.target.closest(".crm-chat-emoji-btn")
      ) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEmojiClick = (emojiData) => {
    setDraft((prev) => prev + (emojiData.emoji || ""));
  };

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, []);

  const loadChatList = useCallback(async () => {
    if (!crmUserId) return;
    setListLoading(true);
    try {
      const list = await fetchChatList(crmUserId);
      setChats((list || []).map(normalizeChat));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load chats");
    } finally {
      setListLoading(false);
    }
  }, [crmUserId]);

  const openConversation = useCallback(
    async (chat) => {
      if (!chat?.chatId || !crmUserId) return;
      const normalized = normalizeChat(chat);
      activeChatIdRef.current = normalized.chatId;
      setActiveChat(normalized);
      setUserRemovedFromActiveChat(false);
      setMessagesLoading(true);
      setMessages([]);
      try {
        joinChatRoom(normalized.chatId);
        const result = await fetchChatMessages(normalized.chatId, {
          userId: crmUserId,
          page: 1,
          limit: 50,
        });
        const sorted = dedupeMessageList(
          (result.messages || [])
            .map((m) => normalizeMessage(m, crmUserId))
            .sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime(),
            ),
        );
        seenMessageIdsRef.current = new Set(
          sorted.map((m) => m._id).filter(Boolean),
        );
        setMessages(sorted);
        markChatReadSocket(normalized.chatId);
        markChatReadRest(normalized.chatId, crmUserId).catch(() => { });
        setChats((prev) =>
          prev.map((c) =>
            c.chatId === normalized.chatId ? { ...c, unreadCount: 0 } : c,
          ),
        );
        scrollToBottom();
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || "Failed to load messages");
      } finally {
        setMessagesLoading(false);
      }
    },
    [crmUserId, scrollToBottom],
  );

  const startChatWithPatient = useCallback(
    async (patientUserId, patientName) => {
      if (!patientUserId || !crmUserId) {
        toast.error("Patient user id is missing");
        return;
      }
      try {
        const data = await startChat(patientUserId, crmUserId);
        const chatPayload = data?.chatId ? data : data?.data || data;
        const chat = normalizeChat({
          ...chatPayload,
          chatName:
            chatPayload?.chatName ||
            patientName ||
            chatPayload?.otherUser?.name,
          otherUser: {
            ...(chatPayload?.otherUser || {}),
            userId: String(
              chatPayload?.otherUser?.userId || patientUserId,
            ),
            name:
              chatPayload?.otherUser?.name ||
              patientName ||
              `Patient ${patientUserId}`,
          },
        });
        setChats((prev) => {
          const exists = prev.some((c) => c.chatId === chat.chatId);
          if (exists) {
            return prev.map((c) =>
              c.chatId === chat.chatId ? { ...c, ...chat } : c,
            );
          }
          return [chat, ...prev];
        });
        await openConversation(chat);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
          "Unable to start chat. Check patient user id.",
        );
      }
    },
    [crmUserId, openConversation],
  );

  // File Attachment selection handler
  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Max 25MB check
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File size must be under 25MB");
      return;
    }

    setAttachmentFile(file);
    if (file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setAttachmentPreview(previewUrl);
    } else {
      setAttachmentPreview(null);
    }
  };

  const handleClearAttachment = () => {
    if (attachmentPreview && attachmentPreview.startsWith("blob:")) {
      URL.revokeObjectURL(attachmentPreview);
    }
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Socket + list bootstrap
  useEffect(() => {
    const token = getToken();
    if (!token || !crmUserId) return undefined;

    connectChatSocket(token);
    setSocketOnline(isChatSocketConnected());

    const unsubStatus = onSosSocketStatus((status) => {
      setSocketOnline(!!status?.connected);
      if (status?.connected) requestPresence();
    });

    loadChatList();

    const unsubMsg = onChatMessage((raw) => {
      const msg = normalizeMessage(raw, crmUserId);
      const chatId = msg.chatId;
      if (!chatId || (!msg.message && !msg.attachment)) return;

      // Drop exact re-broadcasts (same DB id delivered via user room + chat room)
      if (msg._id && seenMessageIdsRef.current.has(msg._id)) {
        if (activeChatIdRef.current === chatId) {
          setMessages((prev) => {
            const hasPendingLocal = prev.some(
              (m) => String(m._id).startsWith("local-") && m.mine,
            );
            if (hasPendingLocal) {
              return prev
                .filter(
                  (m) =>
                    !String(m._id).startsWith("local-") || !m.mine,
                )
                .map((m) =>
                  m._id === msg._id
                    ? { ...m, ...msg, isRead: msg.isRead || m.isRead }
                    : m,
                );
            }
            return prev.map((m) =>
              m._id === msg._id
                ? { ...m, ...msg, isRead: msg.isRead || m.isRead }
                : m,
            );
          });
        }
        return;
      }
      if (msg._id) seenMessageIdsRef.current.add(msg._id);

      setChats((prev) => {
        const idx = prev.findIndex((c) => c.chatId === chatId);
        if (idx === -1) {
          loadChatList();
          return prev;
        }
        const next = [...prev];
        const item = { ...next[idx] };
        const displayPreview =
          msg.message || (msg.attachment ? "📎 Attachment" : "");
        item.lastMessage = displayPreview;
        item.lastMessageAt = msg.createdAt;
        if (activeChatIdRef.current !== chatId && !msg.mine) {
          item.unreadCount = Number(item.unreadCount || 0) + 1;
        }
        next.splice(idx, 1);
        return [item, ...next];
      });

      if (activeChatIdRef.current === chatId) {
        setMessages((prev) => mergeIncomingMessage(prev, msg));
        markChatReadSocket(chatId);
        scrollToBottom();
      }
    });

    const unsubRead = onChatRead((payload) => {
      if (!payload?.chatId) return;
      if (activeChatIdRef.current !== payload.chatId) return;

      const readerId = String(
        payload.readByUserId ??
        payload.userId ??
        payload.readerUserId ??
        "",
      );
      if (!readerId || readerId === String(crmUserId)) return;

      setMessages((prev) =>
        prev.map((m) => (m.mine ? { ...m, isRead: true } : m)),
      );
    });

    const unsubPresence = onPresenceUpdate((p) => {
      if (!p?.userId) return;
      const uid = String(p.userId);
      const online = !!(p.isOnline || p.status === "online");
      setChats((prev) =>
        prev.map((c) => {
          if (c.isGroup) return c;
          if (String(c.otherUser?.userId) !== uid) return c;
          return {
            ...c,
            isOnline: online,
            status: online ? "online" : "offline",
            otherUser: {
              ...c.otherUser,
              isOnline: online,
              status: online ? "online" : "offline",
              lastActivity: p.lastActivity || c.otherUser?.lastActivity,
            },
          };
        }),
      );
      setActiveChat((curr) => {
        if (
          !curr ||
          curr.isGroup ||
          String(curr.otherUser?.userId) !== uid
        )
          return curr;
        return {
          ...curr,
          isOnline: online,
          status: online ? "online" : "offline",
          otherUser: {
            ...curr.otherUser,
            isOnline: online,
            status: online ? "online" : "offline",
          },
        };
      });
    });

    const unsubMemberRemoved = onChatMemberRemoved((payload) => {
      const { chatId, memberId } = payload || {};
      if (!chatId || !memberId) return;

      const currentId = String(crmUserId);
      const targetId = String(memberId);

      if (targetId === currentId) {
        toast.info("You have been removed from the group conversation.");
        if (activeChatIdRef.current === chatId) {
          setUserRemovedFromActiveChat(true);
        }
        setChats((prev) => prev.filter((c) => c.chatId !== chatId));
      } else {
        if (activeChatIdRef.current === chatId) {
          toast.info("A member was removed from this group.");
          setActiveChat((curr) => {
            if (!curr || curr.chatId !== chatId) return curr;
            const nextMembers = (curr.members || []).filter(
              (m) =>
                String(
                  typeof m === "object" ? m.userId || m._id || m.id : m,
                ) !== targetId,
            );
            const nextIds = (curr.memberIds || []).filter(
              (id) => String(id) !== targetId,
            );
            return { ...curr, members: nextMembers, memberIds: nextIds };
          });
        }
        setChats((prev) =>
          prev.map((c) => {
            if (c.chatId !== chatId) return c;
            const nextMembers = (c.members || []).filter(
              (m) =>
                String(
                  typeof m === "object" ? m.userId || m._id || m.id : m,
                ) !== targetId,
            );
            const nextIds = (c.memberIds || []).filter(
              (id) => String(id) !== targetId,
            );
            return { ...c, members: nextMembers, memberIds: nextIds };
          }),
        );
      }
    });

    return () => {
      unsubStatus();
      unsubMsg();
      unsubRead();
      unsubPresence();
      unsubMemberRemoved();
    };
  }, [crmUserId, loadChatList, scrollToBottom]);

  // Open chat from patient list navigation state
  useEffect(() => {
    const state = location.state;
    if (!state?.chatId && !state?.patientUserId) return undefined;

    let cancelled = false;
    const run = async () => {
      if (state.chatId) {
        if (!cancelled) {
          await openConversation({
            chatId: state.chatId,
            chatName: state.patientName,
            otherUser: {
              userId: state.patientUserId,
              name: state.patientName,
            },
          });
        }
      } else if (state.patientUserId) {
        if (!cancelled) {
          await startChatWithPatient(state.patientUserId, state.patientName);
        }
      }
      if (!cancelled) {
        navigate(location.pathname, { replace: true, state: null });
      }
    };

    const t = setTimeout(run, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    location.state,
    location.pathname,
    navigate,
    openConversation,
    startChatWithPatient,
  ]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load chat users from GET /chat/users on mount and whenever searching
  useEffect(() => {
    let active = true;
    setCandidateLoading(true);
    fetchChatUsers({ search: candidateSearch, page: 1, limit: 100 })
      .then((list) => {
        if (active) setCandidateMembers(list || []);
      })
      .catch((err) => console.error("Error fetching chat users:", err))
      .finally(() => {
        if (active) setCandidateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [candidateSearch]);

  const userMap = useMemo(() => {
    const map = {};
    (candidateMembers || []).forEach((m) => {
      if (m.userId) map[String(m.userId)] = m;
    });
    (chats || []).forEach((c) => {
      if (c.otherUser?.userId) {
        map[String(c.otherUser.userId)] = {
          userId: String(c.otherUser.userId),
          name: c.otherUser.name,
          image: c.otherUser.profileImage,
          phone: c.otherUser.status,
        };
      }
      if (Array.isArray(c.members)) {
        c.members.forEach((m) => {
          if (m && typeof m === "object" && m.userId) {
            map[String(m.userId)] = m;
          }
        });
      }
    });
    return map;
  }, [candidateMembers, chats]);

  const filteredChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chats.filter((c) => {
      if (chatFilterTab === "direct" && c.isGroup) return false;
      if (chatFilterTab === "groups" && !c.isGroup) return false;
      if (!q) return true;
      const name = (c.chatName || c.otherUser?.name || "").toLowerCase();
      const last = (c.lastMessage || "").toLowerCase();
      return name.includes(q) || last.includes(q);
    });
  }, [chats, search, chatFilterTab]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    const file = attachmentFile;

    if (
      (!text && !file) ||
      !activeChat?.chatId ||
      sending ||
      sendingRef.current ||
      userRemovedFromActiveChat
    ) {
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setDraft("");
    setShowEmojiPicker(false);
    handleClearAttachment();

    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const optimistic = normalizeMessage(
      {
        _id: localId,
        chatId: activeChat.chatId,
        message: text,
        attachment: attachmentPreview || (file ? URL.createObjectURL(file) : null),
        attachmentName: file?.name,
        attachmentType: file?.type,
        attachmentSize: file?.size,
        senderUserId: crmUserId,
        senderName: "You",
        createdAt: new Date().toISOString(),
        isMine: true,
        isRead: false,
        isGroup: activeChat.isGroup,
      },
      crmUserId,
    );

    setMessages((prev) => [...prev, optimistic]);
    setChats((prev) => {
      const next = prev.filter((c) => c.chatId !== activeChat.chatId);
      return [
        {
          ...activeChat,
          lastMessage: text || (file ? "📎 Attachment" : ""),
          lastMessageAt: optimistic.createdAt,
          unreadCount: 0,
        },
        ...next,
      ];
    });
    scrollToBottom();

    try {
      // If uploading an attachment, send via multipart/form-data REST API
      if (file) {
        const restResult = await sendChatMessageRest({
          message: text,
          chatId: activeChat.chatId,
          contactUserId: activeChat.otherUser?.userId,
          attachment: file,
          userId: crmUserId,
        });
        const confirmedRaw =
          restResult?.message && typeof restResult.message === "object"
            ? restResult.message
            : restResult?.data && typeof restResult.data === "object"
              ? restResult.data
              : typeof restResult === "object"
                ? restResult
                : {};
        const confirmed = normalizeMessage(
          {
            ...confirmedRaw,
            attachmentName: confirmedRaw.attachmentName || file?.name,
            attachmentType: confirmedRaw.attachmentType || file?.type,
            attachmentSize: confirmedRaw.attachmentSize || file?.size,
            message: confirmedRaw.message || text,
            chatId: activeChat.chatId,
            senderUserId: crmUserId,
            isMine: true,
          },
          crmUserId,
        );
        if (confirmed._id) seenMessageIdsRef.current.add(confirmed._id);
        setMessages((prev) => {
          const hasLocal = prev.some((m) => m._id === localId);
          if (hasLocal) {
            return prev.map((m) => (m._id === localId ? confirmed : m));
          }
          return mergeIncomingMessage(prev, confirmed);
        });
      } else {
        // Plain text message: prefer Socket (or fallback to REST)
        let sent = false;
        if (!activeChat.isGroup) {
          sent = sendChatMessageSocket({
            message: text,
            chatId: activeChat.chatId,
            contactUserId: activeChat.otherUser?.userId,
          });
        }
        if (!sent) {
          const restResult = await sendChatMessageRest({
            message: text,
            chatId: activeChat.chatId,
            contactUserId: activeChat.otherUser?.userId,
            userId: crmUserId,
          });
          const confirmedRaw =
            restResult?.message && typeof restResult.message === "object"
              ? restResult.message
              : restResult?.data && typeof restResult.data === "object"
                ? restResult.data
                : typeof restResult === "object"
                  ? restResult
                  : {};
          const confirmed = normalizeMessage(
            {
              ...confirmedRaw,
              message: confirmedRaw.message || text,
              chatId: activeChat.chatId,
              senderUserId: crmUserId,
              isMine: true,
            },
            crmUserId,
          );
          if (confirmed._id) seenMessageIdsRef.current.add(confirmed._id);
          setMessages((prev) => {
            const hasLocal = prev.some((m) => m._id === localId);
            if (hasLocal) {
              return prev.map((m) => (m._id === localId ? confirmed : m));
            }
            return mergeIncomingMessage(prev, confirmed);
          });
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to send message");
      setMessages((prev) => prev.filter((m) => m._id !== localId));
      setDraft(text);
      if (file) setAttachmentFile(file);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  // Create Group submission
  const handleCreateGroupSubmit = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (selectedMemberIds.length === 0) {
      toast.error("Please select at least one member to add");
      return;
    }

    setCreatingGroup(true);
    try {
      const response = await createGroupChat({
        name: newGroupName.trim(),
        memberIds: selectedMemberIds,
        userId: crmUserId,
      });
      const data = response?.data || response;
      const createdChatId = data?.chatId || data?.chat?.chatId || data?.id;

      // Call POST /chat/group/add-member endpoint
      if (createdChatId && selectedMemberIds.length > 0) {
        try {
          await addGroupMembers({
            userId: crmUserId,
            chatId: createdChatId,
            memberIds: selectedMemberIds,
          });
        } catch (addErr) {
          console.log("Notice calling add-member endpoint:", addErr);
        }
      }

      toast.success("Group created successfully!");
      setIsCreateGroupOpen(false);
      setNewGroupName("");
      setSelectedMemberIds([]);

      const newGroup = normalizeChat({
        ...data,
        chatName: newGroupName.trim(),
        isGroup: true,
        status: "group",
        admin: crmUserId,
        memberIds: [String(crmUserId), ...selectedMemberIds.map(String)],
      });

      setChats((prev) => [newGroup, ...prev]);
      await openConversation(newGroup);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to create group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleAddMembersToActiveGroup = async () => {
    if (!activeChat?.chatId || addMemberSelectedIds.length === 0) return;
    setAddingMembers(true);
    try {
      await addGroupMembers({
        userId: crmUserId,
        chatId: activeChat.chatId,
        memberIds: addMemberSelectedIds,
      });
      toast.success("Members added to group successfully!");
      const updatedMemberIds = Array.from(
        new Set([...(activeChat.memberIds || []), ...addMemberSelectedIds]),
      );
      setActiveChat((prev) => ({
        ...prev,
        memberIds: updatedMemberIds,
      }));
      setChats((prev) =>
        prev.map((c) =>
          c.chatId === activeChat.chatId
            ? { ...c, memberIds: updatedMemberIds }
            : c,
        ),
      );
      setAddMemberSelectedIds([]);
      setAddMemberSearch("");
      setIsAddMemberSectionOpen(false);
    } catch (err) {
      console.error("Error adding members to group:", err);
      toast.error(err?.response?.data?.message || "Failed to add members");
    } finally {
      setAddingMembers(false);
    }
  };

  // Remove member from group
  const handleRemoveMember = async (memberId, memberName) => {
    if (!activeChat?.chatId || !memberId) return;

    const result = await Swal.fire({
      title: `Remove ${memberName || "member"}?`,
      text: "This user will be removed from the group conversation.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove",
    });

    if (!result.isConfirmed) return;

    try {
      await removeGroupMember({
        chatId: activeChat.chatId,
        memberId,
        userId: crmUserId,
      });
      toast.success("Member removed from group");
      setActiveChat((curr) => {
        if (!curr) return curr;
        const nextMembers = (curr.members || []).filter(
          (m) =>
            String(
              typeof m === "object" ? m.userId || m._id || m.id : m,
            ) !== String(memberId),
        );
        const nextIds = (curr.memberIds || []).filter(
          (id) => String(id) !== String(memberId),
        );
        return { ...curr, members: nextMembers, memberIds: nextIds };
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to remove member");
    }
  };

  // Leave group
  const handleLeaveGroup = async () => {
    if (!activeChat?.chatId) return;

    const result = await Swal.fire({
      title: "Leave group?",
      text: "You will no longer receive messages from this group.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, leave group",
    });

    if (!result.isConfirmed) return;

    try {
      await removeGroupMember({
        chatId: activeChat.chatId,
        memberId: crmUserId,
        userId: crmUserId,
      });
      toast.success("You left the group");
      setIsGroupInfoOpen(false);
      setChats((prev) => prev.filter((c) => c.chatId !== activeChat.chatId));
      setActiveChat(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to leave group");
    }
  };

  const toggleSelectMember = (userId) => {
    const id = String(userId);
    setSelectedMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="page-wrapper crm-messages-layout">
      <div className="content">
        <div className="crm-chat-page">
          {/* Top Header */}
          <div className="crm-chat-header-bar">
            <div>
              <h4 className="page-title mb-0">Messages</h4>
              <p className="crm-chat-subtitle">
                Direct and group messaging in real time
              </p>
            </div>
            <div
              className={`crm-chat-presence ${socketOnline ? "is-online" : "is-offline"
                }`}
              title={
                socketOnline
                  ? "Connected to real-time chat server"
                  : "Connecting…"
              }
            >
              <span className="crm-chat-presence-dot" />
              {socketOnline ? "Online" : "Connecting"}
            </div>
          </div>

          {/* Main Shell */}
          <div className="crm-chat-shell">
            {/* Sidebar */}
            <aside className="crm-chat-sidebar">
              <div className="crm-chat-sidebar-header">
                <div className="crm-chat-sidebar-top-row">
                  <div className="crm-chat-filter-pills">
                    <button
                      type="button"
                      className={`crm-chat-filter-pill ${chatFilterTab === "all" ? "active" : ""
                        }`}
                      onClick={() => setChatFilterTab("all")}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`crm-chat-filter-pill ${chatFilterTab === "direct" ? "active" : ""
                        }`}
                      onClick={() => setChatFilterTab("direct")}
                    >
                      Direct
                    </button>
                    <button
                      type="button"
                      className={`crm-chat-filter-pill ${chatFilterTab === "groups" ? "active" : ""
                        }`}
                      onClick={() => setChatFilterTab("groups")}
                    >
                      Groups
                    </button>
                  </div>

                  <button
                    type="button"
                    className="crm-chat-new-group-btn"
                    onClick={() => setIsCreateGroupOpen(true)}
                    title="Create a new group"
                  >
                    <i className="fa fa-plus" /> New Group
                  </button>
                </div>

                <div className="crm-chat-search">
                  <i className="fa fa-search" />
                  <input
                    type="search"
                    placeholder="Search conversations…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Chat list */}
              <div className="crm-chat-list">
                {listLoading ? (
                  <div className="crm-chat-empty">Loading chats…</div>
                ) : filteredChats.length === 0 ? (
                  <div className="crm-chat-empty">
                    No conversations found.
                    <span>
                      Start a chat from Patients or create a New Group.
                    </span>
                  </div>
                ) : (
                  filteredChats.map((chat) => {
                    const active = activeChat?.chatId === chat.chatId;
                    return (
                      <button
                        type="button"
                        key={chat.chatId}
                        className={`crm-chat-list-item ${active ? "active" : ""
                          }`}
                        onClick={() => openConversation(chat)}
                      >
                        <div className="crm-chat-avatar-wrap">
                          <ChatAvatar
                            name={chat.chatName || chat.otherUser?.name}
                            src={chat.otherUser?.profileImage}
                            isGroup={chat.isGroup}
                          />
                          {chat.isGroup ? (
                            <span className="crm-chat-status-dot group">
                              <i className="fa fa-users" />
                            </span>
                          ) : (
                            <span
                              className={`crm-chat-status-dot ${chat.isOnline ? "online" : "offline"
                                }`}
                            />
                          )}
                        </div>
                        <div className="crm-chat-list-meta">
                          <div className="crm-chat-list-top">
                            <strong>
                              {chat.chatName || chat.otherUser?.name}
                              {chat.isGroup && (
                                <span className="crm-chat-group-badge">
                                  Group
                                </span>
                              )}
                            </strong>
                            <small>{formatTime(chat.lastMessageAt)}</small>
                          </div>
                          <div className="crm-chat-list-bottom">
                            <span className="crm-chat-preview">
                              {chat.lastMessage || "No messages yet"}
                            </span>
                            {chat.unreadCount > 0 ? (
                              <em className="crm-chat-unread">
                                {chat.unreadCount > 99
                                  ? "99+"
                                  : chat.unreadCount}
                              </em>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* Conversation View */}
            <section className="crm-chat-main">
              {!activeChat ? (
                <div className="crm-chat-placeholder">
                  <div className="crm-chat-placeholder-icon">
                    <i className="fa fa-comments" />
                  </div>
                  <h5>Select a conversation</h5>
                  <p>
                    Choose a chat from the left, or create a group to start
                    messaging.
                  </p>
                </div>
              ) : (
                <>
                  {/* Thread Header */}
                  <div className="crm-chat-thread-header">
                    <div className="crm-chat-avatar-wrap">
                      <ChatAvatar
                        name={activeChat.chatName || activeChat.otherUser?.name}
                        src={activeChat.otherUser?.profileImage}
                        size="lg"
                        isGroup={activeChat.isGroup}
                      />
                    </div>
                    <div className="crm-chat-thread-user">
                      <h5>
                        {activeChat.chatName || activeChat.otherUser?.name}
                        {activeChat.isGroup && (
                          <span className="crm-chat-group-badge">Group</span>
                        )}
                      </h5>
                      <small
                        className={
                          activeChat.isGroup
                            ? "text-muted"
                            : activeChat.isOnline
                              ? "text-online"
                              : "text-offline"
                        }
                      >
                        {activeChat.isGroup
                          ? "Group Conversation"
                          : activeChat.isOnline
                            ? "Online"
                            : "Offline"}
                      </small>
                    </div>

                    <div className="crm-chat-thread-actions">
                      {activeChat.isGroup && (
                        <button
                          type="button"
                          className="crm-chat-header-btn"
                          onClick={() => {
                            setIsGroupInfoOpen(true);
                            if (!activeChat.memberIds || activeChat.memberIds.length === 0) {
                              setIsAddMemberSectionOpen(true);
                            }
                          }}
                          title="View Group Details & Members"
                        >
                          <i className="fa fa-info-circle" /> Group Info
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Removed from group banner if applicable */}
                  {userRemovedFromActiveChat && (
                    <div className="crm-chat-removed-banner">
                      <i className="fa fa-exclamation-triangle" />
                      <span>
                        You have been removed from this group and cannot send
                        messages.
                      </span>
                    </div>
                  )}

                  {/* Messages Feed */}
                  <div className="crm-chat-thread-body">
                    {messagesLoading ? (
                      <div className="crm-chat-empty">Loading messages…</div>
                    ) : messages.length === 0 ? (
                      <div className="crm-chat-empty">
                        No messages yet. Say hello.
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isImage = isImageAttachment(
                          msg.attachment,
                          msg.attachmentType,
                          msg.attachmentName,
                        );
                        const fileUrl = resolveAttachmentUrl(msg.attachment);

                        return (
                          <div
                            key={msg._id}
                            className={`crm-chat-bubble-row ${msg.mine ? "mine" : "theirs"
                              }`}
                          >
                            <div className="crm-chat-bubble">
                              {/* Sender name for group chats */}
                              {!msg.mine && activeChat.isGroup && (
                                <span className="crm-chat-sender-name">
                                  {msg.senderName ||
                                    `Member ${msg.senderUserId?.slice(-4) || ""}`}
                                </span>
                              )}

                              {/* Attachment Card / Image */}
                              {msg.attachment && (
                                <div className="crm-chat-attachment-box">
                                  {isImage ? (
                                    <div
                                      className="crm-chat-attachment-image-wrap"
                                      onClick={() => setLightboxImage(fileUrl)}
                                      title="Click to enlarge"
                                    >
                                      <img
                                        src={fileUrl}
                                        alt={msg.attachmentName || "Attachment"}
                                        className="crm-chat-attachment-image"
                                      />
                                      <div className="crm-chat-attachment-overlay">
                                        <i className="fa fa-search-plus" />
                                      </div>
                                    </div>
                                  ) : (
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download
                                      className="crm-chat-file-card"
                                    >
                                      <i
                                        className={`crm-chat-file-icon ${getFileIcon(
                                          msg.attachment,
                                          msg.attachmentType,
                                          msg.attachmentName,
                                        )}`}
                                      />
                                      <div className="crm-chat-file-info">
                                        <span className="crm-chat-file-name">
                                          {msg.attachmentName ||
                                            "Attached Document"}
                                        </span>
                                        <span className="crm-chat-file-action">
                                          <i className="fa fa-download" />{" "}
                                          Download
                                          {msg.attachmentSize
                                            ? ` (${formatFileSize(
                                              msg.attachmentSize,
                                            )})`
                                            : ""}
                                        </span>
                                      </div>
                                    </a>
                                  )}
                                </div>
                              )}

                              {/* Text message */}
                              {msg.message && <p>{msg.message}</p>}

                              <div className="crm-chat-meta">
                                <time>{formatTime(msg.createdAt)}</time>
                                <MessageTicks msg={msg} />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer with Attachment & Emoji support */}
                  {!userRemovedFromActiveChat && (
                    <div className="crm-chat-composer-wrap">
                      {/* Emoji Picker Popover */}
                      {showEmojiPicker && (
                        <div
                          className="crm-chat-emoji-picker-container"
                          ref={emojiPickerRef}
                        >
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            autoFocusSearch={false}
                            width={320}
                            height={380}
                            previewConfig={{ showPreview: false }}
                          />
                        </div>
                      )}

                      {/* Attachment Preview */}
                      {attachmentFile && (
                        <div className="crm-chat-attachment-preview">
                          {attachmentPreview ? (
                            <img
                              src={attachmentPreview}
                              alt="Preview"
                              className="crm-chat-attachment-preview-thumb"
                            />
                          ) : (
                            <div className="crm-chat-attachment-preview-icon">
                              <i
                                className={getFileIcon(
                                  attachmentFile.name,
                                  attachmentFile.type,
                                )}
                              />
                            </div>
                          )}
                          <div className="crm-chat-attachment-preview-meta">
                            <div className="crm-chat-attachment-preview-name">
                              {attachmentFile.name}
                            </div>
                            <div className="crm-chat-attachment-preview-size">
                              {formatFileSize(attachmentFile.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="crm-chat-attachment-remove-btn"
                            onClick={handleClearAttachment}
                            title="Remove attachment"
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      )}

                      <form className="crm-chat-composer" onSubmit={handleSend}>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleAttachmentChange}
                        />

                        <button
                          type="button"
                          className="crm-chat-attach-btn"
                          onClick={() => fileInputRef.current?.click()}
                          title="Attach file or image"
                          disabled={sending}
                        >
                          <i className="fa fa-paperclip" />
                        </button>

                        <button
                          type="button"
                          className={`crm-chat-emoji-btn ${showEmojiPicker ? "active" : ""
                            }`}
                          onClick={() => setShowEmojiPicker((prev) => !prev)}
                          title="Insert emoji"
                          disabled={sending}
                        >
                          <i className="fa fa-smile" />
                        </button>

                        <input
                          type="text"
                          placeholder={
                            attachmentFile
                              ? "Add a caption (optional)…"
                              : "Type a message…"
                          }
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          disabled={sending}
                          autoComplete="off"
                        />

                        <button
                          type="submit"
                          className="crm-chat-send-btn"
                          disabled={(!draft.trim() && !attachmentFile) || sending}
                          title="Send"
                        >
                          {sending ? (
                            <i className="fa fa-spinner fa-spin" />
                          ) : (
                            <i className="fa fa-paper-plane" />
                          )}
                        </button>
                      </form>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* CREATE GROUP MODAL */}
      {isCreateGroupOpen && (
        <div className="crm-chat-modal-backdrop">
          <div className="crm-chat-modal">
            <div className="crm-chat-modal-header">
              <h4>Create New Group</h4>
              <button
                type="button"
                className="crm-chat-modal-close-btn"
                onClick={() => setIsCreateGroupOpen(false)}
              >
                <i className="fa fa-times" />
              </button>
            </div>

            <form onSubmit={handleCreateGroupSubmit}>
              <div className="crm-chat-modal-body">
                <div className="crm-chat-form-group">
                  <label>Group Name</label>
                  <input
                    type="text"
                    className="crm-chat-input-text"
                    placeholder="e.g. Cardiology Support Team"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    required
                  />
                </div>

                <div className="crm-chat-form-group">
                  <label>
                    Select Members ({selectedMemberIds.length} selected)
                  </label>

                  {/* Selected member chips */}
                  {selectedMemberIds.length > 0 && (
                    <div className="crm-chat-selected-tags">
                      {selectedMemberIds.map((id) => {
                        const m = candidateMembers.find(
                          (c) => String(c.userId) === String(id),
                        );
                        return (
                          <span key={id} className="crm-chat-selected-tag">
                            {m?.name || `User ${id}`}
                            <button
                              type="button"
                              onClick={() => toggleSelectMember(id)}
                            >
                              <i className="fa fa-times" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Member search bar */}
                  <div className="crm-chat-member-search">
                    <i className="fa fa-search" />
                    <input
                      type="search"
                      placeholder="Search patient / contact name…"
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                    />
                  </div>

                  {/* Candidates List */}
                  <div className="crm-chat-candidates-list">
                    {candidateLoading ? (
                      <div className="crm-chat-empty">Loading candidates…</div>
                    ) : candidateMembers.length === 0 ? (
                      <div className="crm-chat-empty">No members found.</div>
                    ) : (
                      candidateMembers.map((cand) => {
                        const isSelected = selectedMemberIds.includes(
                          String(cand.userId),
                        );
                        return (
                          <div
                            key={cand.userId}
                            className={`crm-chat-candidate-row ${isSelected ? "selected" : ""
                              }`}
                            onClick={() => toggleSelectMember(cand.userId)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => { }}
                              style={{ pointerEvents: "none" }}
                            />
                            <ChatAvatar name={cand.name} src={cand.image} />
                            <div className="crm-chat-candidate-info">
                              <span className="crm-chat-candidate-name">
                                {cand.name}
                              </span>
                              <span className="crm-chat-candidate-sub">
                                {cand.phone || cand.email || `ID: ${cand.userId}`}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="crm-chat-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsCreateGroupOpen(false)}
                  disabled={creatingGroup}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creatingGroup || !newGroupName.trim() || selectedMemberIds.length === 0}
                >
                  {creatingGroup ? "Creating…" : "Create Group"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GROUP INFO MODAL */}
      {isGroupInfoOpen && activeChat?.isGroup && (
        <div className="crm-chat-modal-backdrop">
          <div className="crm-chat-modal">
            <div className="crm-chat-modal-header">
              <h4>Group Details</h4>
              <button
                type="button"
                className="crm-chat-modal-close-btn"
                onClick={() => setIsGroupInfoOpen(false)}
              >
                <i className="fa fa-times" />
              </button>
            </div>

            <div className="crm-chat-modal-body">
              <div className="crm-chat-group-info-hero">
                <ChatAvatar
                  name={activeChat.chatName}
                  size="lg"
                  isGroup={true}
                />
                <h5 className="mb-0">{activeChat.chatName}</h5>
                <small className="text-muted">
                  Group ID: {activeChat.chatId}
                </small>
              </div>

              <div className="crm-chat-form-group">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="mb-0 fw-bold">
                    Group Members ({activeChat.memberIds?.length || 0})
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary py-0 px-2"
                    style={{ fontSize: "12px", borderRadius: "4px" }}
                    onClick={() => {
                      setIsAddMemberSectionOpen((prev) => !prev);
                      setAddMemberSearch("");
                    }}
                  >
                    <i className={`fa ${isAddMemberSectionOpen ? "fa-minus" : "fa-plus"} me-1`} />
                    {isAddMemberSectionOpen ? "Hide" : "Add Members"}
                  </button>
                </div>

                {/* Add Member inline selector */}
                {isAddMemberSectionOpen && (
                  <div className="p-2 border rounded bg-light mb-3">
                    <small className="fw-semibold text-primary d-block mb-1">
                      Select Users to Add to Group:
                    </small>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search users to add..."
                        value={addMemberSearch}
                        onChange={(e) => setAddMemberSearch(e.target.value)}
                        style={{ fontSize: "12px" }}
                      />
                    </div>
                    <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                      {candidateMembers
                        .filter((cand) => {
                          const isAlreadyMember = activeChat.memberIds?.includes(String(cand.userId));
                          if (isAlreadyMember) return false;
                          const q = addMemberSearch.trim().toLowerCase();
                          if (!q) return true;
                          const name = String(cand.name || "").toLowerCase();
                          const email = String(cand.email || "").toLowerCase();
                          const phone = String(cand.phone || "").toLowerCase();
                          const uId = String(cand.userId || "").toLowerCase();
                          return name.includes(q) || email.includes(q) || phone.includes(q) || uId.includes(q);
                        })
                        .map((cand) => {
                          const isSelected = addMemberSelectedIds.includes(
                            String(cand.userId),
                          );
                          return (
                            <div
                              key={cand.userId}
                              className="d-flex align-items-center gap-2 p-1 border-bottom"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setAddMemberSelectedIds((prev) =>
                                  prev.includes(String(cand.userId))
                                    ? prev.filter((id) => id !== String(cand.userId))
                                    : [...prev, String(cand.userId)],
                                );
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                              />
                              <ChatAvatar name={cand.name} src={cand.image} />
                              <div>
                                <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                                  {cand.name}
                                </div>
                                <div style={{ fontSize: "10px", color: "#666" }}>
                                  {cand.email || cand.phone || `ID: ${cand.userId}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {candidateMembers.filter((cand) => {
                        const isAlreadyMember = activeChat.memberIds?.includes(String(cand.userId));
                        if (isAlreadyMember) return false;
                        const q = addMemberSearch.trim().toLowerCase();
                        if (!q) return true;
                        const name = String(cand.name || "").toLowerCase();
                        const email = String(cand.email || "").toLowerCase();
                        const phone = String(cand.phone || "").toLowerCase();
                        const uId = String(cand.userId || "").toLowerCase();
                        return name.includes(q) || email.includes(q) || phone.includes(q) || uId.includes(q);
                      }).length === 0 && (
                          <div className="text-muted text-center py-2" style={{ fontSize: "12px" }}>
                            {addMemberSearch ? "No matching users found" : "No users available to add"}
                          </div>
                        )}
                    </div>
                    {addMemberSelectedIds.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary w-100 mt-2"
                        disabled={addingMembers}
                        onClick={handleAddMembersToActiveGroup}
                      >
                        {addingMembers
                          ? "Adding..."
                          : `Add Selected (${addMemberSelectedIds.length})`}
                      </button>
                    )}
                  </div>
                )}

                <div className="crm-chat-candidates-list" style={{ maxHeight: "250px", overflowY: "auto" }}>
                  {(!activeChat.memberIds || activeChat.memberIds.length === 0) &&
                    (!activeChat.members || activeChat.members.length === 0) ? (
                    <div className="crm-chat-empty">
                      No member list available.
                    </div>
                  ) : (
                    (activeChat.memberIds && activeChat.memberIds.length > 0
                      ? activeChat.memberIds
                      : activeChat.members.map((m) =>
                        typeof m === "object" ? m.userId || m._id || m.id : m,
                      )
                    ).map((mId) => {
                      const idStr = String(mId);
                      const memberObj =
                        userMap[idStr] ||
                        (typeof mId === "object" ? mId : null) ||
                        (activeChat.members || []).find(
                          (x) =>
                            String(
                              typeof x === "object"
                                ? x.userId || x._id || x.id
                                : x,
                            ) === idStr,
                        );

                      const memberName =
                        memberObj?.name ||
                        memberObj?.patient_name ||
                        (idStr === String(crmUserId) ? "You" : `User ${idStr}`);
                      const memberSub =
                        memberObj?.email ||
                        memberObj?.phone ||
                        `ID: ${idStr}`;
                      const isCurrent = idStr === String(crmUserId);
                      const isAdmin = idStr === String(activeChat.admin);
                      const isCreator =
                        String(activeChat.admin) === String(crmUserId);

                      return (
                        <div
                          key={idStr}
                          className="crm-chat-member-list-item d-flex align-items-center justify-content-between p-2 border-bottom"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <ChatAvatar
                              name={memberName}
                              src={memberObj?.image || memberObj?.profileImage}
                            />
                            <div>
                              <strong style={{ fontSize: "13px" }}>
                                {memberName} {isCurrent && "(You)"}
                              </strong>
                              {isAdmin && (
                                <span
                                  className="badge bg-primary ms-2"
                                  style={{ fontSize: "10px" }}
                                >
                                  Admin
                                </span>
                              )}
                              <div
                                className="text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                {memberSub}
                              </div>
                            </div>
                          </div>

                          {isCreator && !isCurrent && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ fontSize: "11px", padding: "2px 8px" }}
                              onClick={() =>
                                handleRemoveMember(idStr, memberName)
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <button
                type="button"
                className="crm-chat-leave-group-btn"
                onClick={handleLeaveGroup}
              >
                <i className="fa fa-sign-out-alt" /> Leave Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX MODAL */}
      {lightboxImage && (
        <div
          className="crm-chat-lightbox-backdrop"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            className="crm-chat-lightbox-close"
            onClick={() => setLightboxImage(null)}
            title="Close"
          >
            <i className="fa fa-times" />
          </button>

          <img
            src={lightboxImage}
            alt="Enlarged view"
            className="crm-chat-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />

          <div
            className="crm-chat-lightbox-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={lightboxImage}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="crm-chat-lightbox-btn"
            >
              <i className="fa fa-download" /> Open Original
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
